import React, { useState } from 'react';
import { Sun, Moon, Star, Menu, X } from 'lucide-react';
import { ThemeMode } from '../types';
import { useI18n } from '../i18n/I18nContext';
import { SmartLink } from './SmartLink';

interface HeaderProps {
  theme: ThemeMode;
  onToggleTheme: () => void;
  favoritesCount: number;
  onOpenFavorites: () => void;
  activeNav: string;
  setActiveNav: (nav: string) => void;
  onOpenInstallModal?: () => void;
}

export const Header: React.FC<HeaderProps> = ({
  theme,
  onToggleTheme,
  favoritesCount,
  onOpenFavorites,
  activeNav,
  setActiveNav,
  onOpenInstallModal
}) => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const { t, getLangUrl } = useI18n();

  const navItems = [
    { id: 'home', label: t('navHome', 'Home'), href: getLangUrl('/') },
    { id: 'all-tools', label: t('navAllTools', 'All Tools'), href: getLangUrl('/tools') },
    { id: 'categories', label: t('navCategories', 'Categories'), href: getLangUrl('/categories') },
    { id: 'about', label: t('navAbout', 'About'), href: getLangUrl('/about') },
    { id: 'blog', label: t('navBlog', 'Blog'), href: getLangUrl('/blog') },
    { id: 'contact', label: t('navContact', 'Contact'), href: getLangUrl('/contact') },
  ];

  const categoryHubs = [
    { id: 'pdf-tools', label: t('hubPdf', 'PDF Tools'), href: getLangUrl('/pdf-tools') },
    { id: 'image-tools', label: t('hubImage', 'Image Tools'), href: getLangUrl('/image-tools') },
    { id: 'text-tools', label: t('hubText', 'Text Tools'), href: getLangUrl('/text-tools') },
    { id: 'developer-tools', label: t('hubDev', 'Developer Tools'), href: getLangUrl('/developer-tools') },
    { id: 'calculator-tools', label: t('hubCalc', 'Calculators'), href: getLangUrl('/calculator-tools') },
    { id: 'finance-tools', label: t('hubFinance', 'Finance Tools'), href: getLangUrl('/finance-tools') },
  ];

  const isDark = theme === 'dark';

  return (
    <header className={`sticky top-0 z-40 w-full backdrop-blur-xl border-b theme-transition ${
      isDark 
        ? 'bg-[#090b10]/85 border-slate-800/80 text-white' 
        : 'bg-white/90 border-slate-200/90 text-slate-900 shadow-sm shadow-slate-200/50'
    }`}>
      <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8 h-12 sm:h-20 flex items-center justify-between">
        
        {/* Brand Logo */}
        <SmartLink 
          href={getLangUrl('/')}
          onClick={() => setActiveNav('home')}
          className="flex items-center gap-1.5 sm:gap-3 cursor-pointer group select-none"
        >
          <div className="relative flex items-center justify-center group-hover:scale-105 transition-transform duration-200">
            <svg className="w-7 h-7 sm:w-10 sm:h-10" viewBox="0 0 512 512" fill="none" xmlns="http://www.w3.org/2000/svg">
              <defs>
                <linearGradient id="headerLogoGrad" x1="0%" y1="0%" x2="100%" y2="100%">
                  <stop offset="0%" stopColor="#8B5CF6" />
                  <stop offset="100%" stopColor="#7C3AED" />
                </linearGradient>
              </defs>
              <path d="M 172.5 110 H 290 C 367.32 110 430 172.68 430 250 C 430 327.32 367.32 390 290 390 H 172.5 C 151.24 390 134 372.76 134 351.5 C 134 330.24 151.24 313 172.5 313 H 290 C 324.79 313 353 284.79 353 250 C 353 215.21 324.79 187 290 187 H 172.5 C 151.24 187 134 169.76 134 148.5 C 134 127.24 151.24 110 172.5 110 Z" fill="url(#headerLogoGrad)" />
              <rect x="200" y="210" width="46" height="80" rx="23" fill="url(#headerLogoGrad)" />
            </svg>
          </div>
          <div className="flex items-center tracking-tight text-xl font-extrabold font-sans">
            <span className={isDark ? 'text-white font-black text-[16px] sm:text-2xl tracking-tight' : 'text-slate-900 font-black text-[16px] sm:text-2xl tracking-tight'}>DUALIS</span>
            <span className="ml-1 sm:ml-2 text-purple-600 font-black text-[16px] sm:text-2xl tracking-tight">TOOLS</span>
          </div>
        </SmartLink>

        {/* Desktop Navigation Links */}
        <nav className="hidden md:flex items-center space-x-1 lg:space-x-4">
          {navItems.map((item) => {
            const isActive = activeNav === item.id;
            return (
              <SmartLink
                key={item.id}
                href={item.href}
                onClick={() => {
                  setActiveNav(item.id);
                }}
                className={`px-3.5 py-2 rounded-xl text-sm font-semibold transition-all duration-200 ${
                  isActive
                    ? isDark
                      ? 'text-white bg-slate-800/80 font-bold border border-slate-700/60'
                      : 'text-purple-700 bg-purple-100/80 font-bold border border-purple-200/80 shadow-xs'
                    : isDark
                      ? 'text-slate-300 hover:text-white hover:bg-slate-800/40'
                      : 'text-slate-700 hover:text-slate-900 hover:bg-slate-100/80'
                }`}
              >
                {item.label}
              </SmartLink>
            );
          })}
        </nav>

        {/* Right Controls: Theme Toggle & Favorites */}
        <div className="flex items-center gap-1.5 sm:gap-3">
          {/* Theme Toggle Button */}
          <button
            onClick={onToggleTheme}
            aria-label="Toggle Theme"
            className={`p-1.5 sm:px-3.5 sm:py-2 rounded-lg sm:rounded-xl border flex items-center gap-2 text-xs font-semibold transition-all duration-300 ${
              isDark
                ? 'bg-slate-900/90 border-slate-700/80 text-slate-300 hover:text-white hover:border-purple-500/50 hover:bg-slate-800'
                : 'bg-white border-slate-300/90 text-slate-800 hover:text-purple-700 hover:border-purple-300 hover:bg-purple-50/60 shadow-xs'
            }`}
          >
            {isDark ? (
              <>
                <Sun className="w-4 h-4 text-amber-400 transition-transform duration-300 rotate-0" />
                <span className="hidden sm:inline">{t('lightMode', 'Light Mode')}</span>
              </>
            ) : (
              <>
                <Moon className="w-4 h-4 text-purple-600 transition-transform duration-300" />
                <span className="hidden sm:inline">{t('darkMode', 'Dark Mode')}</span>
              </>
            )}
          </button>

          {/* Favorite Button */}
          <button
            onClick={onOpenFavorites}
            className={`p-1.5 sm:px-3.5 sm:py-2 rounded-lg sm:rounded-xl border flex items-center gap-2 text-xs font-bold transition-all duration-200 ${
              isDark
                ? 'bg-slate-900/80 border-slate-700/80 text-white hover:border-purple-500/60 hover:bg-slate-800'
                : 'bg-white border-slate-300/90 text-slate-800 hover:border-purple-300 hover:bg-purple-50/60 shadow-xs'
            }`}
          >
            <Star className={`w-4 h-4 ${favoritesCount > 0 ? 'fill-amber-400 text-amber-400' : isDark ? 'text-slate-400' : 'text-amber-500'}`} />
            <span className="hidden sm:inline">{t('favorite', 'Favorite')}</span>
            {favoritesCount > 0 && (
              <span className="ml-0.5 px-1.5 py-0.5 text-[10px] font-bold rounded-full bg-purple-600 text-white shadow-xs">
                {favoritesCount}
              </span>
            )}
          </button>

          {/* Mobile Menu Toggle Button */}
          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            aria-expanded={mobileMenuOpen}
            aria-label="Toggle navigation menu"
            className={`md:hidden p-1.5 sm:p-2 rounded-lg border ${
              isDark
                ? 'border-slate-800 text-slate-300 hover:bg-slate-800'
                : 'border-slate-200 text-slate-700 hover:bg-slate-100'
            }`}
          >
            {mobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>
      </div>

      {/* Mobile Menu Dropdown */}
      {mobileMenuOpen && (
        <div className={`md:hidden border-b px-4 pt-3 pb-5 space-y-4 animate-in slide-in-from-top duration-200 ${
          isDark ? 'bg-[#0b0e17] border-slate-800 text-slate-100' : 'bg-white border-slate-200 text-slate-900'
        }`}>
          {/* Primary Navigation Links */}
          <div className="space-y-1">
            <div className={`text-[11px] font-bold uppercase tracking-wider px-3 mb-1.5 ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>
              Navigation
            </div>
            {navItems.map((item) => (
              <SmartLink
                key={item.id}
                href={item.href}
                onClick={() => {
                  setActiveNav(item.id);
                  setMobileMenuOpen(false);
                }}
                className={`block w-full text-left px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                  activeNav === item.id
                    ? isDark ? 'bg-purple-900/30 text-purple-300 font-semibold' : 'bg-purple-50 text-purple-700 font-semibold'
                    : isDark ? 'text-slate-300 hover:bg-slate-800' : 'text-slate-700 hover:bg-slate-100'
                }`}
              >
                {item.label}
              </SmartLink>
            ))}
          </div>

          {/* Top Category Hubs */}
          <div className={`pt-3 border-t space-y-1 ${isDark ? 'border-slate-800/80' : 'border-slate-100'}`}>
            <div className={`text-[11px] font-bold uppercase tracking-wider px-3 mb-1.5 ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>
              Tool Hubs
            </div>
            <div className="grid grid-cols-2 gap-1">
              {categoryHubs.map((cat) => (
                <SmartLink
                  key={cat.id}
                  href={cat.href}
                  onClick={() => {
                    setActiveNav(cat.id);
                    setMobileMenuOpen(false);
                  }}
                  className={`block px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${
                    activeNav === cat.id
                      ? isDark ? 'text-purple-400 font-semibold bg-purple-950/40' : 'text-purple-700 font-semibold bg-purple-50'
                      : isDark ? 'text-slate-400 hover:text-slate-200 hover:bg-slate-800/60' : 'text-slate-600 hover:text-slate-900 hover:bg-slate-100'
                  }`}
                >
                  {cat.label}
                </SmartLink>
              ))}
            </div>
          </div>
        </div>
      )}
    </header>
  );
};

